import { db } from '../../sdk_initialization';
import {
  collection,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  Timestamp,
  increment,
  setDoc,
} from 'firebase/firestore';

const COLLECTION = 'attendeeCapacity';

/**
 * Get total purchased attendee count and total amount paid for all vendors for an event
 * @param {string} eventId - Event ID
 * @returns {Promise<{ totalPurchased: number, totalAmountPaidCents: number }>} - Totals for all vendors
 */
export async function getTotalPurchasedCapacityAndAmountByEvent(eventId) {
  const records = await listCapacityByEvent(eventId);
  let totalPurchased = 0;
  let totalAmountPaidCents = 0;
  for (const rec of records) {
    const count = rec.purchasedAdditionalAttendees || 0;
    const priceCents = rec.pricePerAdditionalAttendeeCents || 0;
    totalPurchased += count;
    totalAmountPaidCents += count * priceCents;
  }
  return { totalPurchased, totalAmountPaidCents };
}

/**
 * Get or create attendee capacity document for a vendor/event
 * Document ID format: {vendorId}_{eventId}
 * 
 * @param {string} vendorId - Vendor ID
 * @param {string} eventId - Event ID
 * @returns {Promise<Object>} - Capacity document
 */
export async function getAttendeeCapacity(vendorId, eventId) {
  const docId = `${vendorId}_${eventId}`;
  const docRef = doc(db, COLLECTION, docId);
  const docSnap = await getDoc(docRef);
  
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() };
  }
  
  // Return default values if document doesn't exist
  return {
    id: docId,
    vendorId,
    eventId,
    pricePerAdditionalAttendeeCents: 0,
    purchasedAdditionalAttendees: 0,
    createdAt: null,
    updatedAt: null,
  };
}

/**
 * Get total allowed attendees for a vendor/event
 * Includes both free attendees (from booths) and purchased additional attendees
 * 
 * @param {string} vendorId - Vendor ID
 * @param {string} eventId - Event ID
 * @param {number} boothCount - Number of booths vendor has
 * @param {number} freeAttendeesPerBooth - Free attendees per booth from event config
 * @returns {Promise<Object>} - { freeAttendees, purchasedAttendees, totalAllowed }
 */
export async function getTotalAllowedAttendees(vendorId, eventId, boothCount, freeAttendeesPerBooth) {
  const capacity = await getAttendeeCapacity(vendorId, eventId);
  const freeAttendees = boothCount * freeAttendeesPerBooth;
  const purchasedAttendees = capacity.purchasedAdditionalAttendees || 0;
  
  return {
    freeAttendees,
    purchasedAttendees,
    totalAllowed: freeAttendees + purchasedAttendees,
    pricePerAdditionalAttendeeCents: capacity.pricePerAdditionalAttendeeCents || 0
  };
}

/**
 * Add purchased attendee capacity
 * This is called after successful payment
 * 
 * @param {string} vendorId - Vendor ID
 * @param {string} eventId - Event ID
 * @param {number} count - Number of additional attendees purchased
 * @param {string} transactionId - Transaction ID for reference
 * @returns {Promise<void>}
 */
export async function addPurchasedCapacity(vendorId, eventId, count, transactionId = null) {
  const docId = `${vendorId}_${eventId}`;
  const docRef = doc(db, COLLECTION, docId);
  const docSnap = await getDoc(docRef);

  // Get price per booth and price per additional attendee paid in cents
  let pricePerBoothCents = null;
  let pricePerAdditionalAttendeeCents = null;
  try {
    // Try to get event price from events collection
    const eventDocRef = doc(db, 'events', eventId);
    const eventDocSnap = await getDoc(eventDocRef);
    if (eventDocSnap.exists()) {
      // Booth price logic (for booth records)
      let boothPrice = eventDocSnap.data().boothPrice;
      if (boothPrice) {
        if (boothPrice > 10) {
          pricePerBoothCents = Math.round(boothPrice);
        } else {
          pricePerBoothCents = Math.round(boothPrice * 100);
        }
      }
      // Additional attendee price logic (for attendee purchases)
      let additionalAttendeePrice = eventDocSnap.data().additionalAttendeePrice;
      if (additionalAttendeePrice) {
        if (additionalAttendeePrice > 10) {
          pricePerAdditionalAttendeeCents = Math.round(additionalAttendeePrice);
        } else {
          pricePerAdditionalAttendeeCents = Math.round(additionalAttendeePrice * 100);
        }
      }
    }
  } catch (e) {
    pricePerBoothCents = null;
    pricePerAdditionalAttendeeCents = null;
  }

  if (docSnap.exists()) {
    // Document exists, increment the count
    await updateDoc(docRef, {
      purchasedAdditionalAttendees: increment(count),
      updatedAt: Timestamp.now(),
      lastTransactionId: transactionId,
      ...(pricePerBoothCents !== null ? { pricePerBoothCents } : {}),
      ...(pricePerAdditionalAttendeeCents !== null ? { pricePerAdditionalAttendeeCents } : {}),
    });
  } else {
    // Create new document
    await setDoc(docRef, {
      vendorId,
      eventId,
      purchasedAdditionalAttendees: count,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      lastTransactionId: transactionId,
      ...(pricePerBoothCents !== null ? { pricePerBoothCents } : {}),
      ...(pricePerAdditionalAttendeeCents !== null ? { pricePerAdditionalAttendeeCents } : {}),
    });
  }
}

/**
 * Decrease purchased attendee capacity (when refunding)
 * This is called after successful refund
 * 
 * @param {string} vendorId - Vendor ID
 * @param {string} eventId - Event ID
 * @param {number} count - Number of additional attendees being refunded
 * @returns {Promise<void>}
 */
export async function decreasePurchasedCapacity(vendorId, eventId, count) {
  const docId = `${vendorId}_${eventId}`;
  const docRef = doc(db, COLLECTION, docId);
  const docSnap = await getDoc(docRef);
  
  if (docSnap.exists()) {
    const currentCount = docSnap.data().purchasedAdditionalAttendees || 0;
    const newCount = Math.max(0, currentCount - count); // Don't go below 0
    
    await updateDoc(docRef, {
      purchasedAdditionalAttendees: newCount,
      updatedAt: Timestamp.now(),
    });
  }
  // If document doesn't exist, there's nothing to decrease
}


/**
 * List all capacity records for a vendor (across all events)
 * 
 * @param {string} vendorId - Vendor ID
 * @returns {Promise<Array>} - List of capacity records
 */
export async function listCapacityByVendor(vendorId) {
  const q = query(
    collection(db, COLLECTION),
    where('vendorId', '==', vendorId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * List all capacity records for an event (admin use)
 * 
 * @param {string} eventId - Event ID
 * @returns {Promise<Array>} - List of capacity records
 */
export async function listCapacityByEvent(eventId) {
  const q = query(
    collection(db, COLLECTION),
    where('eventId', '==', eventId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * Manually adjust attendee capacity (admin only)
 * 
 * @param {string} vendorId - Vendor ID
 * @param {string} eventId - Event ID
 * @param {number} newCount - New count to set
 * @returns {Promise<void>}
 */
export async function setAttendeeCapacity(vendorId, eventId, newCount) {
  const docId = `${vendorId}_${eventId}`;
  const docRef = doc(db, COLLECTION, docId);
  
  await setDoc(docRef, {
    vendorId,
    eventId,
    purchasedAdditionalAttendees: newCount,
    updatedAt: Timestamp.now(),
  }, { merge: true });
}
